import { Box, Center, Flex, IconButton, Portal, Text } from '@chakra-ui/react';
import { Button } from '@time-line/components/common';
import { Bag, VerifiedTick } from '@time-line/components/icons';
import { useCartCount } from 'hooks/cart/useCartCount';
import { useCartUI } from 'hooks/cart/useCartUI';
import { useRef } from 'react';

import { SHOP_NOW_CTA_LABEL, SUBSCRIBE_AND_SAVE_CTA_LABEL } from './constants';
import { CtaButton } from './CtaButton';
import { useStickyCtaVisibility } from './useStickyCtaVisibility';

type StickyCtaVariantProps =
  | {
      type: 'link';
      href: string;
      label: string;
    }
  | {
      type: 'anchor';
      anchorId: string;
      availableForSale?: boolean;
      isSubscription: boolean;
      label?: string;
    }
  | {
      type: 'scrollToTop';
      isSubscription: boolean;
    };

export type StickyCtaProps = StickyCtaVariantProps & {
  showCart?: boolean;
};

export function StickyCta(props: StickyCtaProps) {
  const { showCart = false, ...ctaProps } = props;
  const heroEndRef = useRef<HTMLDivElement>(null);
  const { toggleCart } = useCartUI();
  const cartCount = useCartCount();

  const { anchorId, isSubscription, stickyButton } = ((
    cta: StickyCtaVariantProps,
  ) => {
    if (cta.type === 'link') {
      const { href, label } = cta;
      return {
        anchorId: undefined,
        isSubscription: false,
        stickyButton: (
          <Button
            as="a"
            href={href}
            size={{ base: 'md', lg: 'sm' }}
            w={{ base: 'full', lg: 'auto' }}
            flexShrink={0}
          >
            {label}
          </Button>
        ),
      };
    }

    if (cta.type === 'scrollToTop') {
      return {
        anchorId: undefined,
        isSubscription: cta.isSubscription,
        stickyButton: (
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            size={{ base: 'md', lg: 'sm' }}
            w={{ base: 'full', lg: 'auto' }}
            flexShrink={0}
          >
            {cta.isSubscription
              ? SUBSCRIBE_AND_SAVE_CTA_LABEL
              : SHOP_NOW_CTA_LABEL}
          </Button>
        ),
      };
    }

    return {
      anchorId: cta.anchorId,
      isSubscription: cta.isSubscription,
      stickyButton: (
        <CtaButton
          size={{ base: 'md', lg: 'sm' }}
          w={{ base: 'full', lg: 'auto' }}
          flexShrink={0}
          disabled={cta.availableForSale === false}
          anchorId={cta.anchorId}
        >
          {cta.label ??
            (cta.isSubscription
              ? SUBSCRIBE_AND_SAVE_CTA_LABEL
              : SHOP_NOW_CTA_LABEL)}
        </CtaButton>
      ),
    };
  })(ctaProps);

  const isVisible = useStickyCtaVisibility(heroEndRef, anchorId);

  return (
    <>
      <div ref={heroEndRef} />
      <Portal>
        <Box
          className="hero_sticky-cta"
          position="fixed"
          left={0}
          right={0}
          bottom={{ base: 0, lg: 'm' }}
          zIndex={4}
          display="flex"
          justifyContent={{ lg: 'center' }}
          px={{ lg: 'sm' }}
          pointerEvents="none"
        >
          <Box
            pointerEvents={isVisible ? 'auto' : 'none'}
            aria-hidden={!isVisible}
            inert={!isVisible || undefined}
            w={{ base: 'full', lg: 'auto' }}
            transition="transform 0.2s ease-in-out"
            transform={`translateY(${isVisible ? '0px' : '200px'})`}
            bgColor="gray.50"
            borderTopWidth={{ base: 1, lg: 0 }}
            borderColor="blackAlpha.200"
            borderRadius={{ lg: 'md' }}
            boxShadow={{
              lg: '0 0 0 1px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px rgba(0, 0, 0, 0.02), 0px 1px 4px 0px rgba(0, 0, 0, 0.06), 0px 6px 24px 0px rgba(0, 0, 0, 0.06)',
            }}
            pl="sm"
            pr={{ base: 'sm', lg: 'xs' }}
            py="2xs"
            pb={{
              base: 'calc(var(--timeline-space-2xs) + env(safe-area-inset-bottom, 0px))',
              lg: '2xs',
            }}
          >
            <Flex gap={{ base: '2xs', lg: 'sm' }} alignItems="center">
              <Flex
                alignItems="center"
                gap="4xs"
                display={{ base: 'none', lg: 'flex' }}
                flexShrink={0}
              >
                <VerifiedTick boxSize="16px" />
                <Text size="detail" fontWeight="medium" color="gray.600">
                  30 day money-back guarantee
                  <Text as="span" size="detail" color="blackAlpha.700">
                    {'\u00A0\u00A0·\u00A0\u00A0Free US Shipping'}
                    {isSubscription &&
                      '\u00A0\u00A0·\u00A0\u00A0Pause or cancel at any time'}
                  </Text>
                </Text>
              </Flex>
              <Flex
                alignItems="center"
                gap="xs"
                flexShrink={0}
                w={{ base: 'full', lg: 'auto' }}
              >
                <Box flex={{ base: 1, lg: 'unset' }} minW={0}>
                  {stickyButton}
                </Box>
                {showCart && cartCount > 0 && (
                  <IconButton
                    variant="link"
                    aria-label="Cart"
                    color="gray.600"
                    flexShrink={0}
                    onClick={() => toggleCart(true)}
                    icon={
                      <Box position="relative">
                        <Center
                          as="span"
                          position="absolute"
                          zIndex={1}
                          top="-2px"
                          left="55%"
                          bgColor="red.300"
                          borderRadius="40px"
                          minW="15px"
                          h="15px"
                          px="4px"
                          fontFamily="mono"
                          fontSize="9px"
                          lineHeight={1}
                          color="gray.50"
                          letterSpacing={0}
                        >
                          {cartCount}
                        </Center>
                        <Bag
                          width="sm!important"
                          sx={{
                            path: {
                              vectorEffect: 'non-scaling-stroke',
                            },
                          }}
                        />
                      </Box>
                    }
                  />
                )}
              </Flex>
            </Flex>
          </Box>
        </Box>
      </Portal>
    </>
  );
}
